import { useState } from "react";
import { Link } from "wouter";
import { useListProducts, useCreateProduct, getListProductsQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { motion } from "framer-motion";
import { Loader2, CreditCard, Wallet, ExternalLink } from "lucide-react";
import { useInfiniteItems } from "@/hooks/useInfiniteItems";
import blueSportsCar from "@assets/IMG_7254_1779819286654.jpeg";
import { useToast } from "@/hooks/use-toast";
import { useWallet } from "@/hooks/useWallet";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";

const CATEGORIES = [
  { value: "all",       label: "All Categories" },
  { value: "avatar",    label: "Avatars"       },
  { value: "vehicle",   label: "Vehicles"      },
  { value: "property",  label: "Property"      },
  { value: "wearable",  label: "Wearables"     },
  { value: "furniture", label: "Furniture"     },
  { value: "art",       label: "Art"           },
  { value: "merch",     label: "Merch"         },
];

const CHAINS = [
  { value: "all",   label: "All Chains" },
  { value: "ISM",   label: "ISM Chain"  },
  { value: "ETH",   label: "Ethereum"   },
  { value: "MATIC", label: "Polygon"    },
  { value: "SOL",   label: "Solana"     },
];

// Per-product image galleries (product id → ordered image URLs)
const PRODUCT_GALLERIES: Record<number, string[]> = {
  36: [
    "/illz-new-world-city-sunset.png",
    "/illz-new-world-city-dusk.jpg",
    "/illz-new-world-city-morning.png",
    "/illz-new-world-city-day.png",
  ],
  37: [
    "/farrah-artist-cover.jpeg",
    "/farrah-artist-brush.jpeg",
    "/farrah-artist-easel.jpeg",
    "/farrah-artist-irl.jpeg",
    "/farrah-artist-print.jpeg",
  ],
};

export default function Marketplace() {
  const [category, setCategory] = useState<string>("all");
  const [blockchain, setBlockchain] = useState<string>("all");
  const [isListOpen, setIsListOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isConnected } = useWallet();
  const { startCheckout, startContentCheckout, isLoading: checkoutLoading } = useStripeCheckout();

  const { data: products, isLoading } = useListProducts({
    category: category !== "all" ? category : undefined,
    blockchain: blockchain !== "all" ? blockchain : undefined,
  });

  const { visibleItems, hasMore, loadMore, total } = useInfiniteItems(products, 8);

  const createProduct = useCreateProduct();

  const handleBuy = (product: typeof visibleItems[number]) => {
    const usdAmount = (product as any).priceUsd as number | null | undefined;
    const productType = (product as any).productType ?? "digital";

    if (productType === "content_access" && (product.id === 43 || product.id === 44)) {
      startContentCheckout(product.id);
      return;
    }

    // Always use Stripe for USD-priced items — wallet connection doesn't block card checkout.
    if (usdAmount && usdAmount > 0) {
      startCheckout({
        productName: product.name,
        productType,
        amountUsd: usdAmount,
        metadata: { productId: String(product.id), blockchain: product.blockchain },
      });
    } else if (isConnected) {
      // Crypto wallet path — no USD price, try on-chain
      toast({
        title: "Transaction Initiated",
        description: `Approve the transaction in your wallet to purchase ${product.name}.`,
      });
    } else {
      // Crypto-only item — prompt wallet connection
      toast({
        title: "Wallet Required",
        description: `${product.name} is a crypto-only asset. Connect your wallet to purchase.`,
        variant: "destructive",
      });
    }
  };

  const handleListProduct = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    createProduct.mutate({
      data: {
        name: formData.get("name") as string,
        category: formData.get("category") as string,
        price: Number(formData.get("price")),
        currency: formData.get("currency") as string,
        blockchain: formData.get("blockchain") as string,
        imageUrl: formData.get("imageUrl") as string || "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=2564&auto=format&fit=crop",
        description: formData.get("description") as string,
      }
    }, {
      onSuccess: () => {
        toast({ title: "Product Listed", description: "Your digital asset is now live on the marketplace." });
        queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
        setIsListOpen(false);
      },
      onError: () => {
        toast({ title: "Listing Failed", description: "There was an error listing your product.", variant: "destructive" });
      }
    });
  };

  return (
    <div className="min-h-screen bg-background text-foreground pb-24">
      {/* Hero */}
      <div className="relative h-64 md:h-96 w-full overflow-hidden">
        <img src={blueSportsCar} alt="Marketplace Hero" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tighter uppercase mb-4 text-white">Global Marketplace</h1>
          <p className="text-xl text-muted-foreground max-w-2xl text-gray-300">Acquire exclusive digital assets. Own your reality on-chain.</p>
        </div>
      </div>

      <div className="container mx-auto px-4 mt-8">
        {/* ── Filters bar — scrollable on mobile ── */}
        <div className="mb-10 bg-card/40 p-4 rounded-xl border border-border">
          <div className="flex flex-col gap-3 sm:gap-0 sm:flex-row sm:items-center sm:justify-between">
            {/* Category + chain chips — horizontal scroll on small screens */}
            <div className="overflow-x-auto pb-1 no-scrollbar">
              <div className="flex gap-2 w-max">
                {CATEGORIES.map(c => (
                  <button
                    key={c.value}
                    onClick={() => setCategory(c.value)}
                    className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-mono border transition-colors ${
                      category === c.value
                        ? "bg-primary/20 border-primary text-primary"
                        : "border-white/10 text-muted-foreground hover:border-white/30 hover:text-white"
                    }`}
                    data-testid={`filter-cat-${c.value}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Chain select + search + list */}
            <div className="flex gap-2 flex-shrink-0 flex-wrap sm:flex-nowrap">
              <div className="overflow-x-auto pb-1 no-scrollbar sm:hidden w-full">
                <div className="flex gap-2 w-max">
                  {CHAINS.map(ch => (
                    <button
                      key={ch.value}
                      onClick={() => setBlockchain(ch.value)}
                      className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-mono border transition-colors ${
                        blockchain === ch.value
                          ? "bg-primary/20 border-primary text-primary"
                          : "border-white/10 text-muted-foreground hover:border-white/30 hover:text-white"
                      }`}
                    >
                      {ch.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Desktop chain select */}
              <div className="hidden sm:block w-40">
                <Select value={blockchain} onValueChange={setBlockchain}>
                  <SelectTrigger data-testid="select-blockchain">
                    <SelectValue placeholder="Blockchain" />
                  </SelectTrigger>
                  <SelectContent>
                    {CHAINS.map(ch => <SelectItem key={ch.value} value={ch.value}>{ch.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <Input
                type="text"
                placeholder="Search items…"
                className="bg-background border-border w-full sm:w-52"
                data-testid="input-search-market"
              />

              <Dialog open={isListOpen} onOpenChange={setIsListOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/80 font-bold uppercase tracking-widest whitespace-nowrap" data-testid="btn-list-product">
                    List Asset
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px] bg-card border-border">
                  <DialogHeader>
                    <DialogTitle className="uppercase tracking-widest font-bold">List Digital Asset</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleListProduct} className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Asset Name</Label>
                      <Input id="name" name="name" required placeholder="e.g. Cyber Punk Jacket" className="bg-background" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="category">Category</Label>
                        <Select name="category" defaultValue="wearable">
                          <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.filter(c => c.value !== "all").map(c =>
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="blockchain">Blockchain</Label>
                        <Select name="blockchain" defaultValue="ISM">
                          <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CHAINS.filter(c => c.value !== "all").map(c =>
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="price">Price</Label>
                        <Input id="price" name="price" type="number" step="0.01" required placeholder="0.00" className="bg-background" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="currency">Currency</Label>
                        <Select name="currency" defaultValue="ISM">
                          <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ISM">ISM</SelectItem>
                            <SelectItem value="ETH">ETH</SelectItem>
                            <SelectItem value="MATIC">MATIC</SelectItem>
                            <SelectItem value="SOL">SOL</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="imageUrl">Image URL (Optional)</Label>
                      <Input id="imageUrl" name="imageUrl" placeholder="https://…" className="bg-background" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea id="description" name="description" required placeholder="Describe your asset…" className="bg-background resize-none h-24" />
                    </div>
                    <Button type="submit" className="w-full bg-primary hover:bg-primary/80 font-bold uppercase tracking-widest mt-2"
                      disabled={createProduct.isPending} data-testid="btn-submit-listing">
                      {createProduct.isPending ? "Minting…" : "Mint & List Asset"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>

        {/* ── Grid ── */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-[400px] bg-card/50 rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {visibleItems.map((product, idx) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: (idx % 8) * 0.04 }}
                >
                  <Card className="overflow-hidden border-border bg-card/40 hover:border-primary/50 transition-all duration-300 group flex flex-col h-full">
                    <Link href={`/marketplace/${product.id}`}>
                      <div className="relative aspect-square w-full bg-black/50 p-4 flex items-center justify-center cursor-pointer">
                        <img src={product.imageUrl} alt={product.name} className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]" />
                        <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-md px-2 py-1 rounded text-xs font-mono border border-white/10 uppercase text-muted-foreground">
                          {product.category}
                        </div>
                        <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-md px-2 py-1 rounded text-xs font-mono border border-white/10 text-primary font-bold">
                          {product.blockchain}
                        </div>
                      </div>
                    </Link>
                    <CardContent className="p-5 flex-1 flex flex-col justify-between">
                      <div>
                        <h3 className="font-bold text-lg leading-tight group-hover:text-primary transition-colors">{product.name}</h3>
                        <p className="text-xs text-muted-foreground mb-4 mt-1">By {product.sellerName}</p>
                      </div>
                      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                        <div>
                          <p className="text-xs text-muted-foreground uppercase mb-1">Price</p>
                          <p className="font-mono font-bold text-lg">{product.price.toLocaleString()} {product.currency}</p>
                        </div>
                        <Button
                          onClick={() => handleBuy(product)}
                          disabled={checkoutLoading}
                          className="bg-white text-black hover:bg-white/80 font-bold uppercase tracking-wider text-xs"
                          data-testid={`btn-buy-${product.id}`}
                        >
                          {checkoutLoading ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : isConnected ? (
                            <><Wallet className="w-3 h-3 mr-1" />Buy</>
                          ) : (
                            <><CreditCard className="w-3 h-3 mr-1" />Buy</>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}

              {products?.length === 0 && (
                <div className="col-span-full text-center py-24 text-muted-foreground">
                  <p className="text-xl">No products match your criteria.</p>
                </div>
              )}
            </div>

            <div className="mt-10 flex flex-col items-center gap-3">
              {hasMore && (
                <button
                  onClick={loadMore}
                  className="border border-white/20 hover:border-primary hover:text-primary font-mono uppercase tracking-widest px-8 py-2 rounded text-sm transition-colors"
                >
                  Load More Assets
                </button>
              )}
              {!hasMore && total > 0 && (
                <p className="text-xs text-muted-foreground font-mono">Showing all {total} item{total !== 1 ? "s" : ""}</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
